'use client';

import { useMemo, useRef, useState } from 'react';
import { METRICS, type MetricId } from '../lib/metric-definitions';
import type { MetricData } from '../hooks/use-metrics-data';
import { fmtMoney, fmtCount, fmtDateShort, fmtDateLong } from '../lib/format';
import { formatNumber } from '@/shared/utils/number-format';
import { buildSmoothPath } from './smooth-path';

interface Props {
  metrics: Record<string, MetricData>;
  activeMetrics: MetricId[];
  comparePrevious: boolean;
  rangeDays: number;
}

const CHART_W = 1000;
const CHART_H = 320;
const PAD_L = 52;
const PAD_R = 20;
const PAD_T = 16;
const PAD_B = 32;

export function MultiLineChart({ metrics, activeMetrics, comparePrevious, rangeDays }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const seriesList = useMemo(() => {
    return activeMetrics
      .map((id) => ({ id, def: METRICS[id], data: metrics[id] }))
      .filter((s): s is { id: MetricId; def: typeof METRICS[MetricId]; data: MetricData } => !!s.data && !s.data.isSnapshot);
  }, [activeMetrics, metrics]);

  const days = seriesList[0]?.data.series.length || 0;
  const chartW = CHART_W - PAD_L - PAD_R;
  const chartH = CHART_H - PAD_T - PAD_B;

  const { globalMin, globalMax } = useMemo(() => {
    let mn = Infinity;
    let mx = -Infinity;
    for (const s of seriesList) {
      const pts = [
        ...s.data.series.map((p) => p.v),
        ...(comparePrevious ? s.data.prevSeries.map((p) => p.v) : []),
      ];
      for (const v of pts) {
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
    }
    if (mn === Infinity) return { globalMin: 0, globalMax: 1 };
    const pad = (mx - mn) * 0.15 || 1;
    return { globalMin: Math.max(0, mn - pad), globalMax: mx + pad };
  }, [seriesList, comparePrevious]);

  const scaleX = (i: number) => PAD_L + (days <= 1 ? chartW / 2 : (i / (days - 1)) * chartW);
  const scaleY = (v: number) =>
    PAD_T + (1 - (v - globalMin) / (globalMax - globalMin || 1)) * chartH;

  // Build path for a series
  // Cale netezită monotone-cubic (fără overshoot) — vezi smooth-path.ts.
  const buildPath = (points: { v: number }[]) =>
    buildSmoothPath(points.map((p, i) => ({ x: scaleX(i), y: scaleY(p.v) })));

  // Y ticks
  const yTicks = 4;
  const yTickValues: number[] = [];
  for (let i = 0; i <= yTicks; i++) {
    yTickValues.push(globalMax - (i / yTicks) * (globalMax - globalMin));
  }

  // X ticks
  const xTickCount = Math.min(5, days);
  const xTicks: number[] = [];
  for (let i = 0; i < xTickCount; i++) {
    xTicks.push(Math.round((i / Math.max(1, xTickCount - 1)) * (days - 1)));
  }

  const handleMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || days === 0) return;
    const relX = ((e.clientX - rect.left) / rect.width) * CHART_W;
    const pct = (relX - PAD_L) / chartW;
    const idx = Math.max(0, Math.min(days - 1, Math.round(pct * (days - 1))));
    setHoverIdx(idx);
  };
  const handleMouseLeave = () => setHoverIdx(null);

  const tooltipDate = hoverIdx !== null && seriesList.length > 0 ? seriesList[0]!.data.series[hoverIdx]?.t : null;

  const xLabel = (i: number): string => {
    if (rangeDays === 1) {
      return i.toString() + ':00';
    }
    const s = seriesList[0]?.data.series[i];
    if (!s) return '';
    return fmtDateShort(s.t);
  };

  return (
    <div className="relative">
      {/* Tooltip top-right */}
      <div
        className={
          'absolute top-0 right-0 z-[2] bg-neutral-900 text-white rounded-[9px] px-3.5 py-2.5 text-[0.6875rem] shadow-[0_6px_18px_rgba(15,14,15,0.18)] min-w-[200px] transition-opacity duration-150 pointer-events-none ' +
          (hoverIdx !== null ? 'opacity-100' : 'opacity-0')
        }
      >
        <div
          className="text-[0.6563rem] font-semibold uppercase tracking-[0.06em] text-white/60 mb-1.5 pb-1.5 border-b border-white/10"
        >
          {tooltipDate ? fmtDateLong(tooltipDate) : '—'}
        </div>
        <div className="flex flex-col">
          {seriesList.map((s) => {
            const v = hoverIdx !== null ? s.data.series[hoverIdx]?.v || 0 : 0;
            const val = s.def.unit === 'money' ? formatNumber(Math.round(v), 0) + ' RON' : fmtCount(v);
            return (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3.5 py-0.5"
              >
                <span className="inline-flex items-center gap-1.5 text-white/85">
                  <span className="w-[6px] h-[6px] rounded-full" style={{ background: s.def.color }} />
                  {s.def.label}
                </span>
                <span className="font-semibold text-white tabular-nums">{val}</span>
              </div>
            );
          })}
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={'0 0 ' + CHART_W + ' ' + CHART_H}
        preserveAspectRatio="none"
        className="w-full block"
        style={{ height: 320, overflow: 'visible' }}
      >
        {seriesList.length === 0 && (
          <text
            x={CHART_W / 2}
            y={(PAD_T + (CHART_H - PAD_B)) / 2}
            textAnchor="middle"
            fill="#9A9791"
            style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '0.6875rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            className="empty-chart-state"
          >
            Metricile selectate sunt snapshot — vezi valorile în tile-uri
          </text>
        )}
        {/* Grid lines */}
        {yTickValues.map((v, i) => {
          const y = PAD_T + (i / yTicks) * chartH;
          const isBaseline = i === yTicks;
          return (
            <g key={i}>
              <line
                x1={PAD_L}
                y1={y}
                x2={CHART_W - PAD_R}
                y2={y}
                stroke={isBaseline ? '#E8E4D8' : '#F0ECE0'}
                strokeWidth="1"
                strokeDasharray={isBaseline ? undefined : '2 4'}
              />
              <text
                x={PAD_L - 8}
                y={y + 3}
                textAnchor="end"
                className="fill-neutral-400"
                style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '0.5938rem', fontWeight: 500 }}
              >
                {seriesList.some((s) => s.def.unit === 'count') ? fmtCount(v) : fmtMoney(v)}
              </text>
            </g>
          );
        })}

        {/* X ticks */}
        {xTicks.map((idx, i) => {
          const x = scaleX(idx);
          return (
            <g key={i}>
              <line x1={x} y1={CHART_H - PAD_B} x2={x} y2={CHART_H - PAD_B + 4} stroke="#E8E4D8" strokeWidth="1" />
              <text
                x={x}
                y={CHART_H - PAD_B + 18}
                textAnchor="middle"
                className="fill-neutral-500"
                style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '0.625rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                {xLabel(idx)}
              </text>
            </g>
          );
        })}

        {/* Previous period (if compare) */}
        {comparePrevious &&
          seriesList.map((s) => (
            <path
              key={'prev-' + s.id}
              d={buildPath(s.data.prevSeries)}
              fill="none"
              stroke={s.def.color}
              strokeWidth="1.5"
              strokeDasharray="4 5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              opacity="0.35"
            />
          ))}

        {/* Current series */}
        {seriesList.map((s) => (
          <path
            key={'cur-' + s.id}
            d={buildPath(s.data.series)}
            fill="none"
            stroke={s.def.color}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Hover zone */}
        <rect
          x={PAD_L}
          y={PAD_T}
          width={chartW}
          height={chartH}
          fill="transparent"
          style={{ cursor: 'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />

        {/* Crosshair */}
        {hoverIdx !== null && (
          <line
            x1={scaleX(hoverIdx)}
            y1={PAD_T}
            x2={scaleX(hoverIdx)}
            y2={CHART_H - PAD_B}
            stroke="#0F0E0F"
            strokeWidth="1"
            opacity="0.5"
            pointerEvents="none"
          />
        )}

        {/* Hover dots */}
        {hoverIdx !== null &&
          seriesList.map((s) => {
            const v = s.data.series[hoverIdx]?.v || 0;
            return (
              <circle
                key={'dot-' + s.id}
                cx={scaleX(hoverIdx)}
                cy={scaleY(v)}
                r="4"
                fill="#FFFFFF"
                stroke={s.def.color}
                strokeWidth="2"
                pointerEvents="none"
              />
            );
          })}


      </svg>
    </div>
  );
}
